import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useFormikContext } from "formik";
import { ReactComponent as EmptyDisplayImage } from "@assets/images/EmptyDisplayImage.svg";
import FormikFroalaTextEditor from "../../../../../FroalaEditor/children/FormikFroalaTextEditor";
import { handleAiAssistantIcon } from "../../../../../FroalaEditor/children/FroalaHelpers";
import NewAIPrompt from "../../../../../NewAiPrompt";
import CropperProfilePhoto from "../../../../../CropperProfilePhoto/CropperProfilePhoto";
import {
  getUserProfilePath,
  handleFileUpload,
} from "../../../../../../utils/fileUpload";
import { colors } from "../../../../../../utils";
import { getLoggedInUser } from "../../../../../../utils/helpers";
import { saveRecentMedia } from "../../../../../../services/api";
const AddDetailsProfileBoxForm = ({ showError = false }) => {
  const { values, setFieldValue, errors, handleBlur } = useFormikContext();

  const editorRef = useRef(null);
  const fileInputRef = useRef(null); // Ref for the file input

  const [showAiPrompt, setShowAiPrompt] = useState(false);

  const [bio, setBio] = useState(values.bio ?? "");

  const [firstName, setFirstName] = useState(values.firstName ?? "");
  const [lastName, setLastName] = useState(values.lastName ?? "");
  const [checked, setChecked] = useState(values.showNumberOfWheels ?? "");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(values.profile_image ?? "");

  const handleChange = (event) => {
    setChecked(event.target.checked);
    setFieldValue("showNumberOfWheels", event.target.checked);
  };

  const handleFroalaCustomization = (FroalaEditor) => {
    handleAiAssistantIcon(FroalaEditor, setShowAiPrompt);
  };

  const handleTitleChange = (val) => {
    setBio(val);
    setFieldValue("bio", val);
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    file && setUploadedFile(URL.createObjectURL(file));
    file && setShowImageCropper(true);
  };

  const userProfileUploadPath = getUserProfilePath({
    user_id: getLoggedInUser().id,
  });

  const callBackImageCropped = async (imageFile) => {
    setShowImageCropper(false);

    try {
      setLoading(true);
      const { location } = await handleFileUpload(
        imageFile,
        userProfileUploadPath
      );
      await saveRecentMedia(undefined, {
        type: "0",
        url: location,
      });
      setDisplayImg(location);
      setFieldValue("profile_image", location);
      setLoading(false);
    } catch (err) {
      console.error("File upload failed:", err);
      setLoading(false);
    }
  };
  const callBackImageNotCropped = () => setShowImageCropper(false);

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress size={48} style={{ color: "#123051" }} />
        </div>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: "5px",
              mt: 2,
            }}
          >
            <Typography
              sx={{
                color: "#0F0F0F",
                fontSize: "16px",
                fontWeight: "700",
                lineHeight: "20px",
                fontFamily: "DM Sans",
              }}
            >
              Display Image
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {displayImg ? (
                <Box
                  sx={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "2px solid #E1E1E1",
                  }}
                >
                  <img
                    src={displayImg}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ) : (
                <EmptyDisplayImage />
              )}

              <Button
                onClick={handleUploadClick}
                sx={{
                  color: "white",
                  textTransform: "capitalize",
                  fontSize: "16px",
                  fontWeight: "500",
                  background: "#9B5AF8",

                  "&:hover": {
                    background: "#9B5AF885",
                  },
                }}
              >
                Upload
              </Button>

              {/* Hidden input for file selection */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />

              {showImageCropper && (
                <CropperProfilePhoto
                  ImageURL={uploadedFile}
                  callBackImageCropped={callBackImageCropped}
                  callBackImageNotCropped={callBackImageNotCropped}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              mt: 2,
            }}
          >
            {/* PROFILE NAME - full width above first/last name */}
            <Box
              sx={{
                width: "100%",
                mb: 0,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  color: "#0F0F0F",
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "20px",
                  fontFamily: "DM Sans",
                }}
              >
                Profile Name
              </Typography>

              <TextField
                name="username"
                onChange={(e) => {
                  setFieldValue("username", e.target.value);
                }}
                onBlur={handleBlur}
                variant="filled"
                placeholder="Add profile name"
                fullWidth
                value={values.username || ""}
                style={{
                  background: "white",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span
                        style={{
                          fontFamily: "DM Sans",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#565556",
                        }}
                      >
                        {values.username?.length || 0} / 30
                      </span>
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                  style: {
                    fontSize: "14px",
                    height: "48px",
                    alignItems: "center",
                    borderRadius: "8px",
                    border: "1px solid #E1E1E1",
                    backgroundColor: "#FFFFFF",
                    color: "#565556",
                    fontFamily: "DM Sans",
                  },
                  inputProps: {
                    style: {
                      paddingLeft: "9px",
                      paddingTop: "10px",
                    },
                    maxLength: 30,
                  },
                }}
              />

              <Typography mt={1} color={colors.error}>
                {errors.username}
              </Typography>
            </Box>

            {/* First and Last name on one line, equally divided */}
            <Box sx={{ display: "flex", gap: "25px" }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Typography
                  sx={{
                    color: "#0F0F0F",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    fontFamily: "DM Sans",
                  }}
                >
                  First Name*
                </Typography>

                <TextField
                  name="firstName"
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setFieldValue("firstName", e.target.value);
                  }}
                  onBlur={handleBlur}
                  variant="filled"
                  placeholder="Add first name"
                  fullWidth
                  value={firstName}
                  style={{
                    background: "white",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span
                          style={{
                            fontFamily: "DM Sans",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#565556",
                          }}
                        >
                          {firstName?.length || 0} / 25
                        </span>
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                    style: {
                      fontSize: "14px",
                      height: "48px",
                      alignItems: "center",
                      borderRadius: "8px",
                      border: "1px solid #E1E1E1",
                      backgroundColor: "#FFFFFF",
                      color: "#565556",
                      fontFamily: "DM Sans",
                    },
                    inputProps: {
                      style: {
                        paddingLeft: "9px",
                        paddingTop: "10px",
                      },
                      maxLength: 25,
                    },
                  }}
                />
                <Typography mt={1} color={colors.error}>
                  {errors.firstName}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <Typography
                  sx={{
                    color: "#0F0F0F",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    fontFamily: "DM Sans",
                  }}
                >
                  Last Name*
                </Typography>

                <TextField
                  name="lastName"
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setFieldValue("lastName", e.target.value);
                  }}
                  onBlur={handleBlur}
                  variant="filled"
                  placeholder="Add last name"
                  fullWidth
                  value={lastName}
                  style={{
                    background: "white",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span
                          style={{
                            fontFamily: "DM Sans",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#565556",
                          }}
                        >
                          {lastName?.length || 0} / 25
                        </span>
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                    style: {
                      fontSize: "14px",
                      height: "48px",
                      alignItems: "center",
                      borderRadius: "8px",
                      border: "1px solid #E1E1E1",
                      backgroundColor: "#FFFFFF",
                      color: "#565556",
                      fontFamily: "DM Sans",
                    },
                    inputProps: {
                      style: {
                        paddingLeft: "9px",
                        paddingTop: "10px",
                      },
                      maxLength: 25,
                    },
                  }}
                />
                <Typography mt={1} color={colors.error}>
                  {errors.lastName}
                </Typography>
              </Box>
            </Box>
          </Box>

          <FormikFroalaTextEditor
            editorHeight={"70px"}
            name={"bio"}
            id={1}
            // onChange={handleTitleChange}
            handleEditorCustomization={handleFroalaCustomization}
            editorRef={editorRef}
            label={"About"}
            toolbarButtons={[
              "insertLink",
              "customUL",
              "formatOL",
              "bold",
              "italic",
              "underline",
              "specialCharacters",
              "emoticons",
              "aiAssistant",
            ]}
            singleElement
          />

          {/* <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              mt: 2,
            }}
          >
            <Switch
              checked={checked}
              onChange={handleChange}
              inputProps={{ 'aria-label': 'controlled' }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#8F38EF',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#8F38EF',
                },
              }}
            />

            <Typography
              sx={{
                color: '#0F0F0F',
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '20px',
                fontFamily: 'DM Sans',
              }}
            >
              Show number of wheels on my profile box{' '}
            </Typography>
          </Box> */}

          {showAiPrompt &&
            ReactDOM.createPortal(
              <div
                style={{
                  position: "fixed",
                  zIndex: 6522299,
                  right: "35vw",
                  top: "25vh",
                  overflow: "auto",
                }}
              >
                <NewAIPrompt
                  modalWidth="350px"
                  onCloseIconPress={() => setShowAiPrompt(false)}
                />
              </div>,
              document.body // You can also use a different element if needed
            )}
        </>
      )}
    </Box>
  );
};

export default AddDetailsProfileBoxForm;
